import { CURVES, CURVE_SEGMENTS } from '../../level/segments/definitions/SEG_CURVE';
import { arcCurvature, arcPlacement, arcRadius } from '../../level/segments/segmentPath';
import type { SegmentDefinition } from '../../level/segments/SegmentDefinition';
import { RAD_TO_DEG } from '../../utils/math';
import { AssetCategory, GreyboxMaterialId as M, type AssetManifestEntry, type GreyboxMaterialId, type GreyboxPart } from '../AssetTypes';

/**
 * Greybox of the curve modules (SEG_15/16) until the Blender modules exist: the SEG_01
 * cross-section swept along the arc with `arcBox` parts (no gaps to the neighbouring
 * straights – every strip ends exactly on the connector planes):
 *
 *   asphalt ±4.45 m (top 0) · gravel to ±30 m (top −0.02, inner side clipped before the arc
 *   centre) · edge lines ±3.15 m · dashed lane lines ±1.1 m (2 m dash / 4 m period) ·
 *   plastic barriers at ±3.58 m (1.23 m elements, pitch 1.25 m) · fence ±4.2 m with posts
 *   every 3.33 m · chevron boards on the outer fence every 10 m.
 */
const SECTION = {
  shoulder: 4.45,
  groundTo: 30,
  /** Inner gravel stops this far before the arc centre. */
  innerGroundMargin: 1,
  roadThickness: 0.2,
  groundTop: -0.02,
  edgeLine: { x: 3.15, width: 0.12 },
  dash: { x: 1.1, width: 0.12, length: 2, period: 4 },
  paint: 0.004,
  barrier: { x: 3.58, width: 0.42, height: 0.8, length: 1.23, pitch: 1.25 },
  fence: { x: 4.2, thickness: 0.06, bottom: 0.15, top: 1.95, postPitch: 20 / 6, post: 0.12, postHeight: 2 },
  chevron: { width: 1.6, height: 0.8, bottom: 1.1, thickness: 0.06, inset: 0.12 },
} as const;

/** Bent box in path coordinates: lateral center x, bottom y, from s0 to s1. */
function arcBox(curvature: number, x: number, width: number, y: number, height: number, s0: number, s1: number, material: GreyboxMaterialId): GreyboxPart {
  return { shape: 'arcBox', size: [width, height, s1 - s0], position: [x, y + height / 2, (s0 + s1) / 2], material, arc: { curvature } };
}

/** Straight box standing on the arc at (lateral, s), turned with the path tangent. */
function boxOnArc(curvature: number, size: readonly [number, number, number], lateral: number, y: number, s: number, material: GreyboxMaterialId): GreyboxPart {
  const p = arcPlacement(curvature, lateral, y, s);
  return { shape: 'box', size, position: [p.x, y + size[1] / 2, p.z], rotation: [0, p.yaw * RAD_TO_DEG, 0], material };
}

export function curveSegmentGreybox(length: number, angle: number): GreyboxPart[] {
  const S = SECTION;
  const k = arcCurvature(length, angle);
  const innerSign = Math.sign(angle);
  const innerGroundTo = Math.min(S.groundTo, arcRadius(length, angle) - S.innerGroundMargin);
  const parts: GreyboxPart[] = [arcBox(k, 0, 2 * S.shoulder, -S.roadThickness, S.roadThickness, 0, length, M.Track)];

  for (const sign of [-1, 1] as const) {
    const groundTo = sign === innerSign ? innerGroundTo : S.groundTo;
    const groundWidth = groundTo - S.shoulder;
    parts.push(
      arcBox(k, sign * (S.shoulder + groundWidth / 2), groundWidth, S.groundTop - S.roadThickness, S.roadThickness, 0, length, M.TrackSide),
      arcBox(k, sign * S.edgeLine.x, S.edgeLine.width, 0, S.paint, 0, length, M.LaneLine),
      arcBox(k, sign * S.fence.x, S.fence.thickness, S.fence.bottom, S.fence.top - S.fence.bottom, 0, length, M.Fence),
    );
    for (let s = 0; s + S.barrier.length <= length + 1e-6; s += S.barrier.pitch) {
      const gap = (S.barrier.pitch - S.barrier.length) / 2;
      parts.push(arcBox(k, sign * S.barrier.x, S.barrier.width, 0, S.barrier.height, s + gap, s + gap + S.barrier.length, M.Barrier));
    }
    for (let s = 0; s < length - 1e-6; s += S.fence.postPitch) {
      parts.push(boxOnArc(k, [S.fence.post, S.fence.postHeight, S.fence.post], sign * S.fence.x, 0, s, M.FencePost));
    }
    if (sign !== innerSign) {
      // chevron boards on the outer fence, facing the track (sheet: "Verkehrszeichen <<<")
      for (let s = CURVES.chevronSpacing / 2; s < length; s += CURVES.chevronSpacing) {
        parts.push(boxOnArc(k, [S.chevron.thickness, S.chevron.height, S.chevron.width], sign * (S.fence.x - S.chevron.inset), S.chevron.bottom, s, M.Chevron));
      }
    }
  }
  for (const x of [-S.dash.x, S.dash.x]) {
    for (let s = 0; s + S.dash.length <= length + 1e-6; s += S.dash.period) {
      const from = s + (S.dash.period - S.dash.length) / 2;
      parts.push(arcBox(k, x, S.dash.width, 0, S.paint, from, from + S.dash.length, M.LaneLine));
    }
  }
  return parts;
}

/** Blender sources (tools/blender/assets/ENV_Curve*_A.py via tools/blender/segments/curve_module.py). */
const BLENDER_DESIGN: Readonly<Record<string, string>> = {
  SEG_CURVE_LEFT_45_40M: 'ENV_CurveLeft45_A',
  SEG_CURVE_RIGHT_45_40M: 'ENV_CurveRight45_A',
  SEG_CURVE_LEFT_90_60M: 'ENV_CurveLeft90_A',
  SEG_CURVE_RIGHT_90_60M: 'ENV_CurveRight90_A',
};

function curveAsset(segment: SegmentDefinition): AssetManifestEntry {
  const angle = segment.curve?.angle ?? 0;
  return {
    id: segment.visualAssetId,
    category: AssetCategory.TrackModule,
    // Blender module published as track-modules/<id>.glb; the swept greybox stays as fallback.
    source: { kind: 'glb', url: `/assets/models/track-modules/${segment.visualAssetId.toLowerCase()}.glb`, fallback: curveSegmentGreybox(segment.length, angle) },
    collision: { kind: 'none' },
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: `${segment.displayName}: Bogen r = ${arcRadius(segment.length, angle).toFixed(2)} m, Querschnitt wie SEG_01 (Asphalt, Leitwände, Bauzaun), Leitpfeiltafeln außen`,
      status: 'wip',
      triangleBudget: 30000,
      materialBudget: 18,
      blenderSource: `design/05_environment/${BLENDER_DESIGN[segment.id]}/${BLENDER_DESIGN[segment.id]}.blend`,
    },
  };
}

/** Manifest entries of the curve modules (spread into ASSET_MANIFEST). */
export const CURVE_MODULE_ASSETS: readonly AssetManifestEntry[] = CURVE_SEGMENTS.map(curveAsset);
